import { lazy } from 'react';
import {
  LayoutDashboard, BarChart3, Gauge, Sparkles, Activity, TrendingUp,
  Users, KeyRound, EyeOff, FileSignature,
  Building2, Handshake, CreditCard, Inbox,
  Megaphone, Rss, Lightbulb, Database,
  ShieldCheck, MessageSquareWarning, ScrollText,
  HeartPulse, ListChecks, Webhook, FolderSync, Settings, FlaskConical,
} from 'lucide-react';

// Every admin destination lives here. The rail, the mobile sheet, the content
// header and the route table are all generated from this array, so adding a
// panel is a single entry.
export const ADMIN_NAV = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    items: [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard, Component: lazy(() => import('../OverviewPanel')) },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: TrendingUp,
    items: [
      { id: 'analytics', label: 'Platform Analytics', icon: BarChart3,  Component: lazy(() => import('../AnalyticsPanel')) },
      { id: 'usage',     label: 'Usage',              icon: Gauge,      Component: lazy(() => import('../UsageAnalytics')) },
      { id: 'features',  label: 'Feature Analytics',  icon: Sparkles,   Component: lazy(() => import('../FeatureAnalyticsPanel')) },
      { id: 'ait',       label: 'AIT Analytics',      icon: TrendingUp, Component: lazy(() => import('../AitAnalyticsPanel')) },
      { id: 'activity',  label: 'Activity',           icon: Activity,   Component: lazy(() => import('../ActivityAnalytics')) },
    ],
  },
  {
    id: 'people',
    label: 'People & Access',
    icon: Users,
    items: [
      { id: 'users',           label: 'Users',           icon: Users,         Component: lazy(() => import('../UsersPanel')) },
      { id: 'permissions',     label: 'Permissions',     icon: KeyRound,      Component: lazy(() => import('../FeaturePermissionsPanel')) },
      { id: 'chat-visibility', label: 'Chat Visibility', icon: EyeOff,        Component: lazy(() => import('../ClientChatVisibilityPanel')) },
      { id: 'agreements',      label: 'User Agreements', icon: FileSignature, Component: lazy(() => import('../UserAgreementsPanel')) },
    ],
  },
  {
    id: 'clients',
    label: 'Clients & Revenue',
    icon: Building2,
    items: [
      { id: 'clients',          label: 'Clients',          icon: Building2,  Component: lazy(() => import('../ClientsPanel')) },
      { id: 'partners',         label: 'Partners',         icon: Handshake,  Component: lazy(() => import('../PartnerManagement')) },
      { id: 'billing',          label: 'Billing',          icon: CreditCard, Component: lazy(() => import('../BillingPanel')) },
      { id: 'service-requests', label: 'Service Requests', icon: Inbox,      Component: lazy(() => import('../RequestServicesAdminPanel')) },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: Megaphone,
    items: [
      { id: 'announcements', label: 'Announcements', icon: Megaphone, Component: lazy(() => import('../AnnouncementsPanel')) },
      { id: 'news',          label: 'News',          icon: Rss,       Component: lazy(() => import('../NewsAdminPanel')) },
      { id: 'kpros',         label: 'Kpros Tips',    icon: Lightbulb, Component: lazy(() => import('../KprosTipsPanel')) },
      { id: 'rag-sources',   label: 'RAG & Sources', icon: Database,  Component: lazy(() => import('../RagSourcesPanel')) },
    ],
  },
  {
    id: 'trust',
    label: 'Trust & Safety',
    icon: ShieldCheck,
    items: [
      { id: 'security',   label: 'Security',   icon: ShieldCheck,          Component: lazy(() => import('../SecurityPanel')) },
      { id: 'moderation', label: 'Moderation', icon: MessageSquareWarning, Component: lazy(() => import('../ModerationPanel')) },
      { id: 'audit-logs', label: 'Audit Logs', icon: ScrollText,           Component: lazy(() => import('../AuditLogsPanel')) },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings,
    items: [
      { id: 'status',     label: 'Status',           icon: HeartPulse,   Component: lazy(() => import('../AdminStatus')) },
      { id: 'jobs',       label: 'Background Jobs',  icon: ListChecks,   Component: lazy(() => import('../BackgroundJobsPanel')) },
      { id: 'webhooks',   label: 'Webhooks',         icon: Webhook,      Component: lazy(() => import('../WebhooksPanel')) },
      { id: 'sharepoint', label: 'SharePoint Sync',  icon: FolderSync,   Component: lazy(() => import('../SharePointSyncPanel')) },
      { id: 'settings',   label: 'Settings',         icon: Settings,     Component: lazy(() => import('../SystemSettingsPanel')) },
      { id: 'data-lab',   label: 'Data Lab',         icon: FlaskConical, Component: lazy(() => import('../components/datalab/DataLabPanel')) },
    ],
  },
];

export const DEFAULT_PATH = '/dashboard/admin/overview/overview';

export function flatNavItems() {
  return ADMIN_NAV.flatMap((group) => group.items.map((item) => ({ group, item })));
}

export function findNavItem(groupId, itemId) {
  const group = ADMIN_NAV.find((g) => g.id === groupId);
  if (!group) return null;
  const item = group.items.find((i) => i.id === itemId);
  if (!item) return null;
  return { group, item };
}

export function navPath(groupId, itemId) {
  return `/dashboard/admin/${groupId}/${itemId}`;
}
